// Samurai overview map: a thumbnail of the whole drawing with the visible part outlined. Tap or
// drag on it to move the view there.
import type { PuzzleDoc } from '@zinc/shared';
import { memo, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { values, type GridState } from '../../lib/game';
import type { Geometry } from '../../lib/geometry';
import type { Rect } from '../../lib/zoom';
import { cellsPath } from './constraints/Cells';
import { CELL } from './constraints/geom';

const C = {
  cell: '#FFFFFF',
  filled: '#DDD8CE', // Border token: cells with a digit
  border: '#2B2A28', // grid border
  accent: '#2F5FD0',
};

const MapCells = memo(function MapCells({
  doc,
  geometry: g,
  state,
}: {
  doc: PuzzleDoc;
  geometry: Geometry;
  state: GridState;
}) {
  const subs = doc.grid.subGrids ?? [
    { row: 0, col: 0, rows: g.rows, cols: g.cols, boxRows: 0, boxCols: 0 },
  ];
  const vals = values(g, state);
  const filled = g.cells.filter((_, i) => vals[i] != null);
  return (
    <>
      <path d={cellsPath(g.cells)} fill={C.cell} />
      <path d={cellsPath(filled)} fill={C.filled} />
      {subs.map((s, i) => (
        <rect
          key={i}
          x={s.col * CELL}
          y={s.row * CELL}
          width={s.cols * CELL}
          height={s.rows * CELL}
          fill="none"
          stroke={C.border}
          strokeWidth={10}
        />
      ))}
    </>
  );
});

export function Overview({
  doc,
  geometry,
  state,
  frame,
  view,
  onJump,
}: {
  doc: PuzzleDoc;
  geometry: Geometry;
  state: GridState;
  frame: Rect;
  view: Rect;
  /** The point to centre on, as SVG units. */
  onJump: (x: number, y: number) => void;
}) {
  const down = useRef(false);
  const jump = (e: ReactPointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    onJump(
      frame.x + ((e.clientX - r.left) / r.width) * frame.w,
      frame.y + ((e.clientY - r.top) / r.height) * frame.h,
    );
  };
  return (
    <svg
      viewBox={`${frame.x} ${frame.y} ${frame.w} ${frame.h}`}
      role="img"
      aria-label="Overview map: tap to move there"
      className="block w-20 touch-none rounded-[8px] border border-line bg-bg opacity-90 shadow-sm select-none"
      onPointerDown={(e) => {
        down.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        jump(e);
      }}
      onPointerMove={(e) => {
        if (down.current) jump(e);
      }}
      onPointerUp={() => (down.current = false)}
      onPointerCancel={() => (down.current = false)}
    >
      <MapCells doc={doc} geometry={geometry} state={state} />
      <rect
        data-viewport=""
        x={view.x}
        y={view.y}
        width={view.w}
        height={view.h}
        fill={C.accent}
        fillOpacity={0.12}
        stroke={C.accent}
        strokeWidth={16}
      />
    </svg>
  );
}
